import dayjs from 'dayjs';
import { get, groupBy, isEmpty, keys, omit, uniqBy } from 'lodash';
import { DEFAULT_POSITION, layoutTypeToPositionsKey } from 'src/constants';
import {
  ComponentTypes,
  DyProductSource,
  IComponent,
  ILayout,
  ProjectConfigInterface,
} from 'src/interfaces';
import {
  ISchedule,
  IScheduleItem,
  IScheduleItemOverride,
} from 'src/interfaces/Schedule';
import { getSectionSizeAndPositionForLayoutBuilder } from 'src/utils';
import { uid } from 'uid';

import { getPositionsByPositionNumber } from '../scheduleComponentPage/modules/scheduleComponentPageSidebar/ScheduleComponentPageSidebar.helper';
import { getLayerScreensDisclaimerInfo } from '../scheduleComponentPage/ScheduleComponentPage.helper';
import {
  IPositionGridLines,
  IPredefinedSection,
  IScheduledSectionComponent,
  TScreensGridSections,
} from '../scheduleComponentPage/ScheduleComponentPage.interface';

export const generateSectionGroupingKey = ({
  height,
  width,
  topOffset,
  leftOffset,
}: {
  height: number;
  width: number;
  topOffset: number;
  leftOffset: number;
}) => `${height}-${width}-${topOffset}-${leftOffset}`;

export const transformScheduleItemToComponent = (
  scheduleItem: IScheduleItem & { overrides?: IScheduleItemOverride[] },
): IScheduledSectionComponent => {
  const override = scheduleItem?.overrides?.[0];
  const baseConfig = scheduleItem.component?.config || {};
  const overrideConfig = override?.component_config || {};

  const mergedConfig = {
    ...baseConfig,
    ...overrideConfig,
    productList: overrideConfig.productList ?? baseConfig.productList,
  };

  const updatedComponentWithProducts = {
    ...scheduleItem.component,
    config: mergedConfig,
    component_config: mergedConfig,
  } as IComponent;

  return {
    uid: `scheduleItem-${scheduleItem.id}`,
    id: scheduleItem.id,
    assembly_id: -1,
    component: updatedComponentWithProducts,
    component_id: scheduleItem.component_id,
    scheduleStartDate: scheduleItem.start_date,
    scheduleEndDate: scheduleItem.end_date,
    override: override,
    rotation_order: -1,
    backup_order: 0,
    duration: scheduleItem.duration,
    config: scheduleItem.config as IScheduledSectionComponent['config'],
    allow_overrides: !!scheduleItem.allow_overrides,
    isDeletable: false,
    isOverridden: !!override,
    backups: scheduleItem.backups?.map((backup, index) => ({
      uid: `backup-${backup.id}`,
      id: backup.id,
      assembly_id: -1,
      component_id: backup.component_id,
      component: backup.component,
      scheduleStartDate: scheduleItem.start_date,
      scheduleEndDate: scheduleItem.end_date,
      rotation_order: -1,
      backup_order: index + 1,
      duration: backup.duration,
      config: {
        ...backup.config,
        duration: backup.duration,
      } as IScheduledSectionComponent['config'],
    })),
  };
};

export const calculateGridPositionForItem = (
  layout: ILayout | undefined,
  screenSequence: number,
  item: IScheduleItem,
) => {
  const screenGridLine = get(layout, `config.gridLines.${screenSequence - 1}`);

  return screenGridLine
    ? getSectionSizeAndPositionForLayoutBuilder(
        screenGridLine,
        item.width,
        item.height,
        item.left_offset,
        item.top_offset,
      )
    : { x: 0, y: 0, w: 0, h: 0 };
};

export const determineComponentTypeFlags = (item: IScheduleItem) => {
  const isDisclaimer =
    item.component?.component_type?.name === ComponentTypes.DISCLAIMER;
  const isDYR =
    get(item, 'component_config.productsSource') === DyProductSource.DYR;

  return { isDisclaimer, isDYR };
};

export const buildGridSectionFromScheduleItems = (
  scheduleItems: IScheduleItem[],
  screenSequence: number,
  sectionIndex: number,
  layout?: ILayout,
  existingGridSections: IPredefinedSection[] = [],
) => {
  const firstItem = scheduleItems[0];
  const firstOriginalSection = existingGridSections[0];
  const components = scheduleItems.map(transformScheduleItemToComponent);

  /* Check if there's an existing foundation section at the same position, if so, then return the original section data */
  if (firstOriginalSection) {
    return {
      ...firstOriginalSection,
      canBeScheduled: !!components.length,
      components,
    };
  }

  const gridPosition = calculateGridPositionForItem(
    layout,
    screenSequence,
    firstItem,
  );
  const { isDisclaimer, isDYR } = determineComponentTypeFlags(firstItem);

  const newSection = {
    i: uid(),
    ...gridPosition,
    isResizable: true,
    isDraggable: true,
    screenId:
      layout?.screens.find(
        (item) => item.channel_screen_sequence === screenSequence,
      )?.id || 0,
    name: `Section ${sectionIndex + 1}`,
    height: firstItem.height,
    width: firstItem.width,
    topOffset: firstItem.top_offset,
    leftOffset: firstItem.left_offset,
    config: firstItem.section_config as IScheduledSectionComponent['config'],
    isDisclaimer,
    isDYR,
    canBeDeleted: false,
    components,
  };

  return newSection;
};

export const convertScreenItemsToGridSections = (
  itemsByCurrentScreen: IScheduleItem[],
  screenSequence: number,
  layout?: ILayout,
  existingGridSections: IPredefinedSection[] = [],
) => {
  const itemsGroupedBySameSection = groupBy(itemsByCurrentScreen, (item) =>
    generateSectionGroupingKey({
      height: item.height,
      width: item.width,
      topOffset: item.top_offset,
      leftOffset: item.left_offset,
    }),
  );

  const itemGroupingKeys = new Set(Object.keys(itemsGroupedBySameSection));
  const matchingExistingSectionsByKey: Record<string, IPredefinedSection[]> =
    {};
  const nonMatchingExistingSections: IPredefinedSection[] = [];

  for (const section of existingGridSections) {
    const key = generateSectionGroupingKey({
      height: section.height,
      width: section.width,
      topOffset: section.topOffset,
      leftOffset: section.leftOffset,
    });

    if (itemGroupingKeys.has(key)) {
      if (!matchingExistingSectionsByKey[key]) {
        matchingExistingSectionsByKey[key] = [];
      }
      matchingExistingSectionsByKey[key].push(section);
    } else {
      nonMatchingExistingSections.push(section);
    }
  }

  let nonDisclaimerIndex = 0;

  const processedSections = Object.entries(itemsGroupedBySameSection).map(
    ([sectionKey, sectionItems], disclaimerIndex) => {
      const matchingExistingSections =
        matchingExistingSectionsByKey[sectionKey];

      const { isDisclaimer } = determineComponentTypeFlags(sectionItems[0]);
      const sectionIndex = isDisclaimer
        ? disclaimerIndex
        : nonDisclaimerIndex++;

      return buildGridSectionFromScheduleItems(
        sectionItems,
        screenSequence,
        sectionIndex,
        layout,
        matchingExistingSections,
      );
    },
  );

  return [...nonMatchingExistingSections, ...processedSections];
};

export const createScheduleItemOverridesMap = (
  scheduleItemOverrides: IScheduleItemOverride[],
) => {
  const overridesMap = new Map<number, IScheduleItemOverride[]>();

  for (const override of scheduleItemOverrides) {
    if (!overridesMap.has(override.schedule_item_id)) {
      overridesMap.set(override.schedule_item_id, []);
    }
    overridesMap.get(override.schedule_item_id)?.push(override);
  }

  return overridesMap;
};

export const initializePositionBaseStructure = (
  position: IPositionGridLines,
  layout?: ILayout,
) => {
  return getLayerScreensDisclaimerInfo(
    position.gridLines,
    layout?.screens?.map(({ id }) => id) ?? [],
  );
};

export const mergeGridSectionsByScreen = (
  itemsByPosition: IScheduleItem[],
  layout: ILayout | undefined,
  positionStructure: Record<string, IPredefinedSection[]>,
) => {
  const groupedByScreen = groupBy(itemsByPosition, 'channel_screen_sequence');

  Object.entries(groupedByScreen).forEach(([screenSequence, itemsByScreen]) => {
    const formattedScreenSequence = Number(screenSequence) || 0;
    const screenIndex = formattedScreenSequence - 1;
    const existingGridSections = positionStructure[screenIndex] || [];

    const newGridSections = convertScreenItemsToGridSections(
      itemsByScreen,
      formattedScreenSequence,
      layout,
      existingGridSections,
    );

    const disclaimerSections = newGridSections
      .filter((section) => section.isDisclaimer)
      .sort((a, b) => a.name.localeCompare(b.name));

    const nonDisclaimerSections = newGridSections
      .filter((section) => !section.isDisclaimer)
      .sort((a, b) => a.name.localeCompare(b.name));

    positionStructure[screenIndex] = [
      ...disclaimerSections,
      ...nonDisclaimerSections,
    ];
  });
};

export const buildGridSectionsForPosition = (
  position: IPositionGridLines,
  itemsGroupedByPosition: Record<string, IScheduleItem[]>,
  layout?: ILayout,
) => {
  const itemsByPosition = itemsGroupedByPosition[position.position] || [];
  const positionStructure = initializePositionBaseStructure(position, layout);

  mergeGridSectionsByScreen(itemsByPosition, layout, positionStructure);

  return positionStructure;
};

export const getScreensGridSections = (
  layout?: ILayout,
  positions: IPositionGridLines[] = [],
  scheduleItems: IScheduleItem[] = [],
  scheduleItemOverrides: IScheduleItemOverride[] = [],
) => {
  const overridesMap = createScheduleItemOverridesMap(scheduleItemOverrides);

  const scheduleItemsWithOverrides = scheduleItems.map((item) => ({
    ...item,
    overrides: overridesMap.get(item.id) || [],
  }));
  const itemsGroupedByPosition = groupBy(
    scheduleItemsWithOverrides,
    'position',
  );

  const screensGridSections = positions.reduce((acc, position) => {
    acc[position.position] = buildGridSectionsForPosition(
      position,
      itemsGroupedByPosition,
      layout,
    );

    return acc;
  }, {} as TScreensGridSections);

  return screensGridSections;
};

export const getPositionsNumberWhenEditingOrOverriding = (
  projectConfig: Partial<ProjectConfigInterface> | undefined,
  positionKey: keyof ProjectConfigInterface,
  scheduleItems: IScheduleItem[],
) => {
  const maxPositionNumberInSchedule = Math.max(
    ...scheduleItems?.map((item) => item.position),
  );
  const positionNumber =
    Number(projectConfig?.[positionKey]) || DEFAULT_POSITION;

  return Math.max(maxPositionNumberInSchedule, positionNumber);
};

export const mapPositionWithDurationFromScheduleItems = (
  scheduleItems: IScheduleItem[],
): Record<string, number> =>
  scheduleItems?.reduce((acc, scheduleItem) => {
    return {
      ...acc,
      [scheduleItem.position]: scheduleItem.duration,
    };
  }, {});

export const getPositions = (
  layout?: ILayout,
  projectConfig?: Partial<ProjectConfigInterface>,
  schedule?: ISchedule,
): IPositionGridLines[] => {
  const layoutTypeName = layout?.layoutType?.name || '';
  const positionKey = layoutTypeToPositionsKey?.[layoutTypeName];
  const positionNumber = getPositionsNumberWhenEditingOrOverriding(
    projectConfig,
    positionKey,
    schedule?.scheduleItems ?? [],
  );
  const positions = getPositionsByPositionNumber(
    positionNumber,
    mapPositionWithDurationFromScheduleItems(schedule?.scheduleItems ?? []),
  ).map((item) => {
    const gridLines = get(
      schedule,
      `schedule_config.gridLines.${item.position}`,
      get(
        schedule,
        `schedule_config.gridLines.${item.position.toString()}`,
        get(layout, 'config.gridLines', []),
      ),
    );

    return {
      ...item,
      gridLines,
    };
  });

  return positions;
};

export const getScreensWithDisabled = (
  screensGridSections: TScreensGridSections,
  selectedPosition: number,
) => {
  const screensDetails = get(screensGridSections, [selectedPosition]);

  return keys(screensDetails).map((screen) => {
    const items = get(screensDetails, [screen], []);
    const hasAllowedOverride = items.some((item) =>
      isAbleToEditSection(item.components),
    );

    return {
      id: `screen-${selectedPosition}-${screen}`,
      disabled: !hasAllowedOverride,
    };
  });
};
export const isDateRangeValid = (
  startDateStr: string,
  endDateStr: string,
  mainStartDateStr?: string,
  mainEndDateStr?: string | null,
): boolean => {
  const startDate = startDateStr?.trim() ? dayjs(startDateStr) : null;
  const endDate = endDateStr?.trim() ? dayjs(endDateStr) : null;

  if (!startDate || !startDate.isValid()) return false;

  if (endDate) {
    if (!endDate.isValid()) return false;

    if (startDate.isAfter(endDate)) return false;
    if (mainStartDateStr) {
      const mainStartDate = dayjs(mainStartDateStr);
      if (mainStartDate.isValid() && startDate.isBefore(mainStartDate, 'day')) {
        return false;
      }
    }
    if (mainEndDateStr) {
      const mainEndDate = dayjs(mainEndDateStr);
      if (mainEndDate.isValid() && endDate.isAfter(mainEndDate, 'day')) {
        return false;
      }
    }
  }

  return true;
};

export const validateOverrideSchedules = (
  scheduleItemOverrides: IScheduleItemOverride[] = [],
  schedule?: ISchedule,
) => {
  if (isEmpty(scheduleItemOverrides)) {
    return false;
  }

  const scheduleItemsMap = new Map<number, IScheduleItem>();
  schedule?.scheduleItems.forEach((item) => {
    scheduleItemsMap.set(item.id, item);
  });

  return scheduleItemOverrides.every((override) => {
    const scheduleItem = scheduleItemsMap.get(override.schedule_item_id);

    return (
      !!override.start_date &&
      !!override.end_date &&
      isDateRangeValid(
        override.start_date,
        override.end_date,
        scheduleItem?.start_date,
        scheduleItem?.end_date,
      )
    );
  });
};

export const formatOverridePayload = (
  scheduleItemOverrides: IScheduleItemOverride[],
  locationCode: string,
) => {
  return scheduleItemOverrides.map((override) => {
    const isComponentOverride = !!override.component_id;

    return {
      ...omit(
        override,
        isComponentOverride ? ['component', 'component_config'] : ['component'],
      ),
      location_code: locationCode,
      schedule_item_override_variations: uniqBy(
        override.schedule_item_override_variations ?? [],
        'product_variation_product_code',
      ),
    };
  });
};

export const getStartDateParams = (
  scheduleEndDate: string | null | undefined,
) =>
  scheduleEndDate
    ? [`start_date.to=${scheduleEndDate}`, `start_date.null=true`]
    : [];

const areAllScheduleItemsNotAllowedToBeOverridden = (
  scheduleItems: Pick<IScheduleItem, 'allow_overrides' | 'end_date'>[],
) => scheduleItems.every((item) => !item.allow_overrides);

export const areAllScheduleItemsExpired = (
  scheduleItemsForCurrentPosition: Pick<
    IScheduleItem,
    'allow_overrides' | 'end_date'
  >[],
) =>
  scheduleItemsForCurrentPosition
    .filter((s) => s.allow_overrides)
    .every((s) => {
      const endDate =
        typeof s.end_date === 'string' ? dayjs(s.end_date) : s.end_date ?? null;
      const currentDate = dayjs();

      if (endDate === null || endDate.toString() === 'Invalid Date')
        return false;

      const nextDayOfEndDate = endDate.add(1, 'day');

      if (currentDate.isBefore(nextDayOfEndDate)) {
        return false;
      }

      return true;
    });

export const isOverrideButtonDisabled = (
  position: number,
  schedule: ISchedule | undefined,
) => {
  if (!schedule) {
    return false;
  }

  const { scheduleItems } = schedule;
  const scheduleItemsForCurrentPosition = scheduleItems
    .filter((item) => item.position === position)
    .map((item) => ({
      allow_overrides: item.allow_overrides,
      end_date: item.end_date,
    }));

  if (
    scheduleItemsForCurrentPosition.length === 0 ||
    areAllScheduleItemsNotAllowedToBeOverridden(
      scheduleItemsForCurrentPosition,
    ) ||
    areAllScheduleItemsExpired(scheduleItemsForCurrentPosition)
  ) {
    return true;
  }

  return false;
};

export const isAbleToEditSection = (
  sectionComponents: IScheduledSectionComponent[] | undefined,
) => {
  if (!sectionComponents || sectionComponents.length === 0) {
    return false;
  }

  const sectionComponentsData = sectionComponents.map((item) => ({
    allow_overrides: item.allow_overrides ?? false,
    end_date: item.scheduleEndDate ?? null,
  }));

  if (
    areAllScheduleItemsNotAllowedToBeOverridden(sectionComponentsData) ||
    areAllScheduleItemsExpired(sectionComponentsData)
  ) {
    return false;
  }

  return true;
};

export const getOverridePositions = (
  overrides: IScheduleItemOverride[],
  schedule: ISchedule,
) => {
  const { scheduleItems } = schedule;

  return overrides.map(
    (o) =>
      scheduleItems.find((s) => s.id === o.schedule_item_id)
        ?.position as number,
  );
};
